import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const buildFireGraph = (ctx) => {
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  // Ember bed: looping brown noise through a warm lowpass.
  const len = ctx.sampleRate * 4;
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.2;
  }
  const bed = ctx.createBufferSource();
  bed.buffer = buffer;
  bed.loop = true;
  const bedFilter = ctx.createBiquadFilter();
  bedFilter.type = "lowpass";
  bedFilter.frequency.value = 320;
  const bedGain = ctx.createGain();
  bedGain.gain.value = 0.5;
  bed.connect(bedFilter).connect(bedGain).connect(master);

  // Slow movement on the bed filter.
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.07;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 90;
  lfo.connect(lfoGain).connect(bedFilter.frequency);

  // Crackles: random short bandpassed noise bursts.
  const crackleLen = ctx.sampleRate * 0.09;
  const crackleBuf = ctx.createBuffer(1, crackleLen, ctx.sampleRate);
  const cd = crackleBuf.getChannelData(0);
  for (let i = 0; i < crackleLen; i++) {
    cd[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.014));
  }
  let timer = null;
  let running = true;
  const crackle = () => {
    if (!running) return;
    if (Math.random() < 0.75) {
      const src = ctx.createBufferSource();
      src.buffer = crackleBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 900 + Math.random() * 2600;
      bp.Q.value = 6 + Math.random() * 10;
      const g = ctx.createGain();
      g.gain.value = 0.04 + Math.random() * 0.22;
      src.connect(bp).connect(g).connect(master);
      src.start();
    }
    timer = setTimeout(crackle, 60 + Math.random() * 480);
  };

  bed.start();
  lfo.start();
  crackle();

  return {
    master,
    stop() {
      running = false;
      clearTimeout(timer);
      try {
        bed.stop();
        lfo.stop();
      } catch {
        /* already stopped */
      }
    },
  };
};

const EmberAudio = () => {
  const [on, setOn] = useState(false);
  const engineRef = useRef(null);

  const toggle = async () => {
    if (on) {
      const engine = engineRef.current;
      if (engine) {
        engine.master.gain.setTargetAtTime(0, engine.ctx.currentTime, 0.25);
        setTimeout(() => engine.ctx.close(), 900);
        engineRef.current = null;
      }
      setOn(false);
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    await ctx.resume();
    const graph = buildFireGraph(ctx);
    engineRef.current = { ctx, ...graph };
    graph.master.gain.setTargetAtTime(0.5, ctx.currentTime, 0.8);
    setOn(true);
  };

  useEffect(
    () => () => {
      if (engineRef.current) engineRef.current.ctx.close();
    },
    []
  );

  return (
    <motion.button
      onClick={toggle}
      data-testid="hero-audio-toggle"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 3, duration: 1 }}
      className={`group absolute bottom-8 right-6 z-20 flex items-center gap-2.5 border px-4 py-2.5 font-mono text-[10px] font-bold tracking-[0.3em] transition-colors duration-300 lg:right-10 ${
        on
          ? "border-[#E52E2E]/60 text-[#E52E2E]"
          : "border-[#282D37] text-[#5F6573] hover:border-[#E52E2E]/50 hover:text-[#E52E2E]"
      }`}
      aria-label={on ? "Mute ember ambience" : "Play ember ambience"}
    >
      {on ? <Volume2 size={14} /> : <VolumeX size={14} />}
      {on ? "AMBIENCE ON" : "AMBIENCE"}
      {on && <span className="h-1.5 w-1.5 animate-ember-pulse rounded-full bg-[#E52E2E]" />}
    </motion.button>
  );
};

export default EmberAudio;
